import axios from 'axios';
import handleError from '@/composables/handle-error';
import store from '@/store/index.js';
import consoleLog from '@/utils/console-log';

export default class ApiService {
	constructor(options) {
		this.baseUrlConfigName = options.baseUrlConfigName;
		this.requestInterceptors = options.requestInterceptors;
		this.abortControllers = {};
	}

	getBaseUrl() {
		return store.getters[this.baseUrlConfigName];
	}

	get apiClient() {
		const localBaseUrl = this.getBaseUrl();

		if (this._apiClient == undefined) {
			this._apiClient = axios.create({
				baseURL: localBaseUrl,
				headers: {
					Accept: 'application/json'
				}
			});

			this._apiClient.interceptors.request.use(this.requestInterceptors);
		}

		return this._apiClient;
	}

	async get(route, customErrorHandler) {
		let response = await this.apiClient.get(route).catch(error => {
			if (customErrorHandler) {
				customErrorHandler(error, handleError);
			} else {
				handleError(error);
			}
		});

		return this.handleResponse(response);
	}

	/***
	 * route: api endpoint
	 * uid: unique identifier to group the api calls that should be throttled
	 * bindThis: object to bind as this in the handlers
	 * handler: function to handle the response with signature function(response)
	 * finallyHandler: function that always gets called when the request is done
	 * customErrorHandler: custom function to handle an error
	 */
	getWithAbort(
		route,
		uid,
		bindThis,
		handler,
		finallyHandler,
		customErrorHandler
	) {
		const me = this;
		const abortController = new AbortController();
		const boundHandler = handler.bind(bindThis);
		const boundFinallyHandler = finallyHandler.bind(bindThis);
		const boundErrorHandler = customErrorHandler
			? customErrorHandler.bind(bindThis)
			: handleError;

		if (!this.abortControllers[uid]) {
			this.abortControllers[uid] = [];
		}

		this.cancelPending(uid);
		this.abortControllers[uid].push(abortController);

		this.apiClient
			.get(route, {
				signal: abortController.signal
			})
			.then(function(response) {
				if (!abortController.signal.aborted) {
					consoleLog('boundHandler');
					boundHandler(me.handleResponse(response));
				} else {
					consoleLog(
						`getWithAbort ${uid} request cancelled`,
						abortController.signal,
						route
					);
				}
			})
			.catch(error => {
				boundErrorHandler(error);
			})
			.finally(() => {
				boundFinallyHandler();
			});
	}

	cancelPending(uid) {
		if (this.abortControllers[uid]) {
			while (this.abortControllers[uid].length > 0) {
				const controller = this.abortControllers[uid].shift();
				controller.abort('Canceled by new search input');
				consoleLog('cancelPending', controller.signal);
			}
		}
	}

	async getRawResponse(route, options) {
		let response = await this.apiClient.get(route, options);
		return response;
	}

	async post(route, body, customErrorHandler) {
		let response = await this.apiClient.post(route, body).catch(error => {
			if (customErrorHandler) {
				customErrorHandler(error, handleError);
			} else {
				handleError(error);
			}
		});

		return this.handleResponse(response);
	}

	async put(route, body, customErrorHandler) {
		const response = await this.apiClient.put(route, body).catch(error => {
			if (customErrorHandler) {
				customErrorHandler(error, handleError);
			} else {
				handleError(error);
			}
		});
		return this.handleResponse(response);
	}

	async patch(route, body, customErrorHandler) {
		const response = await this.apiClient
			.patch(route, body)
			.catch(error => {
				if (customErrorHandler) {
					customErrorHandler(error, handleError);
				} else {
					handleError(error);
				}
			});
		return this.handleResponse(response);
	}

	async delete(route, body, customErrorHandler) {
		const response = await this.apiClient
			.delete(route, body)
			.catch(error => {
				if (customErrorHandler) {
					customErrorHandler(error, handleError);
				} else {
					handleError(error);
				}
			});
		return this.handleResponse(response);
	}

	handleResponse(resp) {
		let result = resp;

		if (resp) {
			result = resp.data;
		}

		return result;
	}
}
